import {formatCurrency} from '@pinecast/common/helpers/format';
import {Check, Cross} from '@pinecast/common/icons';
import type {DonationPage_podcast} from '@pinecast/gql/__generated_tj__/DonationPage';
import {FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';

import {hasGatedEpisodes, unlocksPrivateEpisodes} from './tipAmounts';

/**
 * Says whether a given amount includes this show's private episodes.
 *
 * Both states are spelled out — the icon carries a shape and the sentence
 * carries the meaning, so neither rests on colour alone. `amount` is null
 * before a listener has chosen, when all we can offer is where the line sits.
 *
 * `standalone` is for the one beside the dropdown, which sits on a line by
 * itself rather than at the foot of a card of includes and so has to say in
 * full what the card's other lines say by context.
 */
const PrivateEpisodeNote = ({
  amount,
  podcast,
  standalone = false,
}: {
  amount: number | null;
  podcast: DonationPage_podcast;
  standalone?: boolean;
}) => {
  const css = useCSS();
  const designColors = useColors();

  if (!hasGatedEpisodes(podcast)) {
    return null;
  }

  const minimum = podcast.privateAccessMinSubscription;
  const from = minimum
    ? formatCurrency(minimum, podcast.tipJarCurrency)
    : formatCurrency(0, podcast.tipJarCurrency);

  const row = css({
    display: 'flex',
    fontSize: '14px',
    gap: '9px',
    alignItems: 'flex-start',
    lineHeight: '20px',
    // Inside an amount card this inherits OptionButton's hover text shadow,
    // which at this size reads as the text going out of focus.
    textShadow: 'none',
  });
  const slot = css({
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    height: '20px',
    justifyContent: 'center',
    width: '18px',
  });
  const muted = css({color: designColors.contentTextMuted});

  if (amount === null) {
    return (
      <span className={row}>
        <span className={muted}>
          {minimum ? (
            <FormattedMessage
              description="Note on the tip jar page saying how much a listener must give each month before private episodes are included. {amount} is a formatted currency amount."
              defaultMessage="Private episodes from {amount} a month"
              values={{amount: from}}
            />
          ) : (
            <FormattedMessage
              description="Note on the tip jar page saying that private episodes come with a contribution of any size"
              defaultMessage="Every amount includes private episodes"
            />
          )}
        </span>
      </span>
    );
  }

  if (unlocksPrivateEpisodes(podcast, amount)) {
    return (
      <span className={row}>
        <span className={slot}>
          <Check size={16} />
        </span>
        <span>
          {standalone ? (
            <FormattedMessage
              description="Note beside the dropdown of other contribution amounts on the tip jar page, when the chosen amount is enough for the show's private episodes"
              defaultMessage="Private episodes included"
            />
          ) : (
            <FormattedMessage
              description="Item in the list of what a tip jar contribution includes"
              defaultMessage="Private episodes"
            />
          )}
        </span>
      </span>
    );
  }

  return (
    <span className={row}>
      <span className={`${slot} ${muted}`}>
        <Cross size={16} />
      </span>
      <span>
        <FormattedMessage
          description="Item in the list of what a tip jar contribution includes, when this amount is below the show's minimum for private episodes"
          defaultMessage="No private episodes"
        />
        <span
          className={css({
            color: designColors.contentTextMuted,
            display: 'block',
            fontSize: '13px',
            lineHeight: '18px',
          })}
        >
          <FormattedMessage
            description="Note under 'No private episodes' saying what a listener would need to give each month to get them. {amount} is a formatted currency amount."
            defaultMessage="Included from {amount} a month"
            values={{amount: from}}
          />
        </span>
      </span>
    </span>
  );
};

export default PrivateEpisodeNote;
